import "@/app/style/videoPlayer.scss";
import { Box, Grid, SxProps, Theme } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import { adobeData, theme } from "@/app/utils/Common";
import screenfull from 'screenfull';
import { PlayCircle } from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import { MultiVideoSource, VIDEOMODE_M, VIDEOMODE_S, VideoMode, VideoQuality, VideoSource, isMultiVideoSourceArray } from "@/app/utils/class/common/video";
import Control, { ControlRef } from "./control";
import { textSize } from "@/app/utils/MixinSize";
import { LANGTYPE_EN, useLanguage } from "@/app/utils/LanguageContext";
import { useMyContext } from "@/app/utils/MyContext";

export interface IVideoWAData extends Pick<WAData, "raceNo" | "contentTitle" | "country" | "buttonName" | "horse"> {
  chgeSrcEventType?: string;
}

export type VideoWAData = IVideoWAData;

export interface PlayerProps {
  sources: VideoSource[] | MultiVideoSource[];
  open: boolean;
  title?: string;
  isMobile: boolean;
  handleClose?: ()=> void;
  autoPlay?: boolean;
  /** Default playback speed. If `enableSpeedChange` is `true`, the video speed will be adjusted based on this playrate. @default 1 */
  defaultPlayrate?: number;
  showCurrentTooltip?: boolean;
  showHoveredTooltip?: boolean;
  width?: string; // work for desktop only
  height?: string; // work for desktop only
  isEmbed?: boolean;
  hideTitleBar?: boolean;
  titleAlign?: "center" | "left";
  titleSx?: SxProps<Theme>;
  WAData?: VideoWAData;
  overridedContentSlot?: React.ReactNode; // will override player if any
  customPlayIconSlot?: React.ReactNode;
  WaKeyForOver5s?: (keyof IVideoWAData)[];
  controlDismissEffect?: 'slideDown';
  /** Allow user change video speed or not @default false */
  enableSpeedChange?: boolean;
}

const initialValue = () => ({
  playing: false,
  muted: false,
  volume: 0.5,
  playedSeconds: 0,
  played: 0,
  playbackRate: 1.0,
  seeking: false,
  buffer : true,
  ended: false,
  loaded: 0,
});

let seekingVol = false;
let count = 0;
let playCount = 0;
const initialWaData = () => ({
  isRequired: false,
  [VIDEOMODE_S]: {
    playCount: 0,
    hasSent: false, // record if wa related to "Video over 5 sec played" is sent
  },
  [VIDEOMODE_M]: {
    playCount: 0,
    hasSent: false, // record if wa related to "Video over 5 sec played" is sent
  }
});
const initialSwitchSrcData = () => ({seconds: -1, playing: true, isSwitching: false});
const initialBlobUrl = () => ({
  [VIDEOMODE_S]: '',
  [VIDEOMODE_M]: '',
});
const DEFAULT_SPEED_WEIGHT = 1;

const LANDSCAPE = 'landscape';
const PORTRAIT = 'portrait';

const Player = (props: PlayerProps) => {
  const {state: myContextState} = useMyContext();
  const {state: lang} = useLanguage();
  const { t } = lang;
  const [orientation, setOrientation] = useState(screen?.orientation?.type || (window.innerWidth > window.innerHeight ? LANDSCAPE : PORTRAIT));
  const controlRef = useRef<ControlRef | null>(null);
  const videoPlayerRef = useRef<ReactPlayer>(null);
  const playerContainerRef = useRef<HTMLDivElement | null>(null); // for fullscreen
  const [videoState, setVideoState] = useState({...initialValue(), curVideoSource: [] as VideoSource[], curQuality: undefined as VideoQuality | undefined, curVideoSourceType: undefined as VideoMode | undefined});
  const [fullscreen, setFullscreen] = useState(false);
  const [showErrMsg, setShowErrMsg] = useState(false);
  const [waState, setWaState] = useState({...initialWaData()});
  const {curVideoSource, curQuality, curVideoSourceType, playing: isPlaying, ended, muted, volume, playbackRate, playedSeconds, played, seeking, buffer, loaded} = videoState;
  const switchSrcData = useRef({...initialSwitchSrcData()}); // for preserve video timestamp on switching video source
  const [videoHeight, setVideoHeight] = useState(undefined);
  const [speedWeight, setSpeedWeight] = useState(DEFAULT_SPEED_WEIGHT);
  
  const [isMi, setIsMi] = useState(false);
  const [blobUrl, setBlobUrl] = useState({...initialBlobUrl()});

  const videoUrl = useMemo(()=>{
    if (!curVideoSource || !curVideoSource.length) return '';
    return (curVideoSource?.find((s) => s.quality === curQuality)||curVideoSource[0]).url;
  }, [curVideoSource, curVideoSourceType, curQuality]);

  // #region special handling for mi browser
  const isMiBrowser = () => {
    if (typeof window === 'undefined') return false;    
    const ua = navigator.userAgent.toLowerCase();
    return ua.indexOf('miuibrowser') > -1;
  };
  const fetchMp4ToBlob = async (videoUrl: string) => {
    const response = await fetch(videoUrl);
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  };
  const releaseBlobUrl = () => {    
    if (isMi) {
      if(blobUrl[VIDEOMODE_M]) URL.revokeObjectURL(blobUrl[VIDEOMODE_M]);
      if(blobUrl[VIDEOMODE_S]) URL.revokeObjectURL(blobUrl[VIDEOMODE_S]);
      setBlobUrl({...initialBlobUrl()});
    }
  }
  useEffect(() => {
    if (!isMi || !videoUrl || !curVideoSourceType) return;
    if (blobUrl[curVideoSourceType]) return;
    fetchMp4ToBlob(videoUrl).then((url)=>setBlobUrl({...blobUrl, [curVideoSourceType]: url}));
  }, [isMi, videoUrl]);
  // #endregion special handling for mi browser

  useEffect(() => {
    setIsMi(isMiBrowser());
    const handleOrientationChange= () => setOrientation(screen?.orientation?.type || (window.innerWidth > window.innerHeight ? PORTRAIT : LANDSCAPE)); // as it is called before rotate, therefore need to reverse the result to get the actual orientation
    const handleFullscreenChange = () => setFullscreen(document.fullscreenElement ? true : false);
    window.addEventListener('orientationchange', handleOrientationChange);
    window.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('fullscreenchange', handleFullscreenChange);
      releaseBlobUrl();
    }
  }, []);
  
  useEffect(() => {
    resetVideoContainer();
    if (props.sources?.length > 0 && props.open) {
      if (switchSrcData.current) switchSrcData.current = initialSwitchSrcData();
      let defaultVideoSource: VideoSource[];
      let defaultVideoSourceType: VideoMode = VIDEOMODE_S; //default
      if (isMultiVideoSourceArray(props.sources)){
        const defaultSource = (props.sources?.find(s => s.default)||props.sources[0]);
        defaultVideoSource = defaultSource.sources;
        defaultVideoSourceType = defaultSource.type;
      } else {
        defaultVideoSource = props.sources
      }
  
      let defaultQuality: VideoQuality | undefined;
      if (defaultVideoSource.length > 0){
        defaultQuality = (defaultVideoSource?.find((s) => s.quality === (props.isMobile ? "sd" : "hd"))||defaultVideoSource[0]).quality;
      }
      setVideoState({ ...videoState, curVideoSource: defaultVideoSource, curQuality: defaultQuality, curVideoSourceType: defaultVideoSourceType, playbackRate: props?.defaultPlayrate || 1});
      resetWAInfo();
    }
  }, [props.open])

  const resetWAInfo = () => {
    playCount = 0;
    setWaState({...initialWaData(), isRequired: window.globalConfig?.WA?.VIDEO_PLAYED_SEC && window.globalConfig?.WA?.VIDEO_PLAYED_SEC != -1 && (props.WAData ? true : false)});
  }

  const resetVideoContainer = ()=>{
    setVideoState({...videoState, ...initialValue()})
    setFullscreen(false);
    setShowErrMsg(false);
    seekingVol = false;
    count = 0;
    setSpeedWeight(DEFAULT_SPEED_WEIGHT);
    setVideoHeight(undefined);
    releaseBlobUrl();
  }
  
  const currentTime = videoPlayerRef.current ? videoPlayerRef.current.getCurrentTime() : 0;
  const duration = videoPlayerRef.current? videoPlayerRef.current.getDuration() : 0;

  const onCloseHandler = (e: any) => {
    e.preventDefault();
    if (props.handleClose)
      props.handleClose?.();
    resetVideoContainer();
  }
  
  const mouseMoveHandler = () => {showControlBar();};

  const showControlBar = () => {
    if (!controlRef.current) return;
    controlRef.current.show(()=>{count = 0});
  }

  // video
  const progressHandler = (state: any) => {
    if (switchSrcData.current.isSwitching) return;
    if (controlRef.current){
      console.log('controlRef.current', {isShown: controlRef.current.isShown, isHovered: controlRef.current.isHovered})
      if (count > 3) {
        controlRef.current.hide(()=>{count = 0});
      } else if (controlRef.current.isShown) {
        if (controlRef.current.isHovered) count = 0;
        else if (!seeking && !seekingVol) count += 1;
      }
    }

    // WA
    if (videoState.playing && !videoState.buffer) {
      playCount += 1;
    }
    if (waState.isRequired && curVideoSourceType && !waState[curVideoSourceType].hasSent && playCount > window.globalConfig?.WA?.VIDEO_PLAYED_SEC) {
      // adobeAnalysis
      adobeData(lang.langType === LANGTYPE_EN ? "en" : "zh-hk", "interaction", {
        // *common data
        pageId: "racing/simulcast",
        eventId: "racing/simulcast_ClickPlayVideo",
        videoFileName: videoPlayerRef.current?.getInternalPlayer().src,
        videoSource: window.location.href,
        // *dynmaic data controlled by WaKeyForOver5s or default
        ...(props?.WaKeyForOver5s ? 
          // *dynmaic data controlled by WaKeyForOver5s
          props?.WaKeyForOver5s?.reduce((acc: any, cur: keyof IVideoWAData) => {
            acc[cur] = props?.WAData?.[cur];
            return acc;
          }, {} as Partial<IVideoWAData>) : 
          { // *default wa if no WaKeyForOver5s
            buttonName: props.WAData?.buttonName,
            raceNo: props.WAData?.raceNo,
            country: props.WAData?.country,
            horse: props.WAData?.horse,
            contentTitle: props.WAData?.contentTitle,
          }
        )
      }, false);
      let newWaState = waState;
      newWaState[curVideoSourceType].hasSent = true;
      setWaState(newWaState);
    }

    if (!seeking) {
      setVideoState({ ...videoState, ...state});
    }
  };
  const bufferStartHandler = () => {console.log('bufferStartHandler'); setVideoState({ ...videoState, buffer: true });}
  const bufferEndHandler = () => {console.log('bufferEndHandler'); setVideoState({ ...videoState, buffer: false });}
  const readyHandler = () => {console.log('readyHandler', switchSrcData.current);
    if(videoPlayerRef.current && switchSrcData.current && switchSrcData.current.seconds > -1){
      console.log('switched', switchSrcData.current);
      const isEnded = switchSrcData.current.seconds >= videoPlayerRef.current.getDuration();
      const targetSec = isEnded ? videoPlayerRef.current.getDuration() : switchSrcData.current.seconds;
      videoPlayerRef.current.seekTo(targetSec, 'seconds'); 
      switchSrcData.current.seconds = -1;
      switchSrcData.current.isSwitching = false;
      setVideoHeight(undefined);
      if (isEnded) {endedHandler()} 
      else {setVideoState({...videoState, playing: switchSrcData.current.playing, ended: false});}
    }else{
      setVideoState({...videoState, buffer: false, playing: switchSrcData.current.playing && props.autoPlay && !showOverridedContent() ? props.autoPlay : false});
    }
  }
  const endedHandler = () => {console.log('endedHandler'); setVideoState({...videoState, playing: false, ended: true}); setSpeedWeight(DEFAULT_SPEED_WEIGHT);}
  const errorHandler = (e: any) => {console.log('errorHandler');
    const msg = e?.message || e?.error?.message || e?.target?.error?.message || "";   
    const isAutoplayError = e?.name === "NotAllowedError" || e?.error?.name === "NotAllowedError" || msg.includes("play() failed because the user didn't interact") || msg.includes("user didn't interact with the document first") || msg.includes("The request is not allowed");   
    const isAbortError = e?.name === "AbortError" || e?.message?.includes('The play() request was interrupted by a new load request');
    if (isAutoplayError) { console.warn("Autoplay blocked"); setVideoState({ ...videoState, playing: false }); return; }
    if (isAbortError) { console.warn('Playback safely bypassed: Resource swap interrupted the initial stream.'); return; }
    setShowErrMsg(true);
    console.error("video player err:", e);
  };
  
  //control
  const playPauseHandler = (e: any) => {
    if (switchSrcData.current.isSwitching) return
    e.preventDefault();
    if (ended) {
      if (!videoPlayerRef.current) return
      videoPlayerRef.current.seekTo(0);
      setVideoState({ ...videoState, playing: true, ended: false, playedSeconds: 0, played: 0});
    } else {
      if (!isPlaying){
        if (!videoPlayerRef.current) return
        videoPlayerRef.current.seekTo(videoState.played);
      } else {
        showControlBar();
      }
      setVideoState({ ...videoState, playing: !isPlaying});
    }
  };
  const seekHandler = (value: any) => setVideoState({ ...videoState, seeking: true, played: parseFloat(value) / 100 });
  const seekMouseUpHandler = (value: any ) => {
    if (!videoPlayerRef.current) return
    if (value >= 100) {
      videoPlayerRef.current.seekTo(duration, "seconds"); // fix jump to 0:01 issue
    } else {      
      videoPlayerRef.current.seekTo(value / 100);
    }
    setVideoState({ ...videoState, seeking: false, ended: false });
  };
  const toggleMute = () => setVideoState({ ...videoState, muted: !muted });
  const volumeChangeHandler = (e: any, value: string) => {
    e.preventDefault();
    seekingVol = true;
    const newVolume = parseFloat(value) / 100;
    setVideoState({...videoState, volume: newVolume, muted: Number(newVolume) === 0 ? true : false});
  };
  const volumeSeekUpHandler = (e: any, value: string) => {
    e.preventDefault();
    seekingVol = false;
    const newVolume = parseFloat(value) / 100;
    setVideoState({...videoState, volume: newVolume, muted: newVolume === 0 ? true : false});
  };
  const preserveCurTime = () => {    
      // Save current progress in seconds
      if (videoPlayerRef.current) {
        switchSrcData.current.seconds = videoPlayerRef.current.getCurrentTime();
        switchSrcData.current.playing = isPlaying;
        switchSrcData.current.isSwitching = true;
        setVideoHeight(videoPlayerRef.current.getInternalPlayer()?.getBoundingClientRect()?.height);
      }
      // reset control count
      count = 0;
  }
  const changeQualityHandler = (qlty: VideoQuality) => {
    if (qlty !== curQuality && !switchSrcData.current.isSwitching) {
      preserveCurTime();
      setVideoState({ ...videoState, curQuality: qlty, playing: false});
    }
  };
  const changeSourceHandler = (type: VideoMode) => {
    if (curVideoSourceType && type !== curVideoSourceType && !switchSrcData.current.isSwitching) {
      if (isMultiVideoSourceArray(props.sources)){
        const selectedSource = (props.sources?.find(s => s.type === type));
        if (selectedSource){
          preserveCurTime();
          if (props.WAData && props.WAData?.chgeSrcEventType) {// adobeAnalysis
            adobeData(lang.langType === LANGTYPE_EN ? "en" : "zh-hk", "interaction", {
              pageId: "racing/simulcast",
              eventId: `racing/simulcast_Click_${props.WAData?.chgeSrcEventType}-${type === VIDEOMODE_S ? "Single" : "Multiple"}`,
              // buttonName: props.WAData?.buttonName,
              raceNo: props.WAData?.raceNo,
              country: props.WAData?.country,
              horse: props.WAData?.horse,
              // videoFileName: videoPlayerRef.current?.getInternalPlayer().src,
              // contentTitle: props.WAData?.contentTitle,
              // videoSource: window.location.href,
            }, false);
          }
          let newWaState = waState;
          newWaState[curVideoSourceType].playCount = playCount;
          playCount = newWaState[selectedSource.type].playCount;
          setWaState(newWaState);
          setVideoState({ ...videoState, curVideoSource: selectedSource.sources, curVideoSourceType: selectedSource.type, playing: false, ended: false});
        }
      }
    }
  };
  const changeSpeedHandler = (spdWeight: number) => {setSpeedWeight(spdWeight);}
  const toggleScreenOrientation = () => {
    // @ts-ignore
    if (!fullscreen && screen?.orientation?.lock) {
      // @ts-ignore
      screen?.orientation?.lock("landscape-primary")
        .catch((err: any) => {
          if (err.name && err.name === 'NotAllowedError') {
            console.error('User did not grant permission to lock screen orientation');
          } else {
            console.error('Error locking screen orientation:', err);
          }
        });
    } else if (screen?.orientation?.unlock) {
      screen?.orientation?.unlock();
    }
  };
  const toggleFullscreenMode = () => {
    if (!fullscreen) {
      if (!playerContainerRef.current) return;
      if (screenfull.isEnabled) {
        screenfull.request(playerContainerRef.current);
      } else if (playerContainerRef.current?.requestFullscreen) { // standard fullscreen api
        playerContainerRef.current?.requestFullscreen();
        // @ts-ignore
      } else if (playerContainerRef.current?.webkitRequestFullscreen) { // chrome/safari
        // @ts-ignore
        playerContainerRef.current?.webkitRequestFullscreen();
        // @ts-ignore
      } else if (playerContainerRef.current?.mozRequestFullscreen) { // firefox
        // @ts-ignore
        playerContainerRef.current?.mozRequestFullscreen();
        // @ts-ignore
      } else if (playerContainerRef.current?.msRequestFullscreen) { // IE/edge
        // @ts-ignore
        playerContainerRef.current?.msRequestFullscreen();
      } else if (videoPlayerRef.current?.getInternalPlayer()?.webkitEnterFullscreen) { // iphone safari
        videoPlayerRef.current?.getInternalPlayer()?.webkitEnterFullscreen();
        setVideoState({...videoState, playing: false});
        return false;
      } else if (videoPlayerRef.current?.getInternalPlayer()?.webkitEnterFullScreen) { // iphone safari
        videoPlayerRef.current?.getInternalPlayer()?.webkitEnterFullScreen();
        setVideoState({...videoState, playing: false});
        return false;
      }
    } else {
      if (screenfull.isFullscreen) {
        screenfull.exit();
      } else if (document.fullscreenElement) {
        document.exitFullscreen();
        // @ts-ignore
      } else if (document.webkitFullscreenElement) {
        // @ts-ignore
        document.webkitExitFullscreen();
        // @ts-ignore
      } else if (document.mozFullscreenElement) {
        // @ts-ignore
        document.mozExitFullscreen();
        // @ts-ignore
      } else if (document.msFullscreenElement) {
        // @ts-ignore
        document.msExitFullscreen();
      }
    }
    return true;
  };
  const toggleFullscreen = () => {
    const updateFullscreen = toggleFullscreenMode(); // iphone use system default control, not using our custom control
    if (props.isMobile) toggleScreenOrientation();
    if (updateFullscreen) setFullscreen(!fullscreen);
  };

  const handleTouch = (e: any, spdWeight: number) => {
    if (!isPlaying) return;
    if (spdWeight !== DEFAULT_SPEED_WEIGHT && speedWeight !== DEFAULT_SPEED_WEIGHT) return; // shouldn't fire another event as user already changed speed
    if (spdWeight === DEFAULT_SPEED_WEIGHT && speedWeight !== DEFAULT_SPEED_WEIGHT && !e?.target.classList.contains("speedWeighted")) return; // shouldn't reset speed as it is not triggerred by same touch
    e?.preventDefault();
    e?.stopPropagation();
    e?.target.classList.toggle("speedWeighted");
    setSpeedWeight(spdWeight);
  };

  const handleTouchEnd = (e: any) => {handleTouch(e, DEFAULT_SPEED_WEIGHT);};

  const speedChangeRegion = (type: 'left'|'right', spdWeight: number) => props?.enableSpeedChange && isPlaying && <div className={`touchable ${type}`} onTouchStart={(e:any)=>handleTouch(e, spdWeight)} onTouchEnd={handleTouchEnd} onTouchCancel={handleTouchEnd} />;

  const commonStyle = () => ({
    ...(!props?.hideTitleBar && {
      display: 'grid',
      gridTemplateRows: `${(props.titleSx as any)?.height ?? '40px'} 1fr`,
    }),
    width: (props.isMobile ? (orientation.toLowerCase().startsWith(LANDSCAPE) ? '100%' : '100%') : props.width) || '100%',
    height: (props.isMobile ? (orientation.toLowerCase().startsWith(LANDSCAPE) ? '100%' : (videoPlayerRef.current || showErrMsg ? (props.height || 'auto') : (props.height || '25%'))) : props.height) || '100%',
    clipPath: 'content-box',
  })
  const renderPlayer = () => 
  showErrMsg ?  
  (
    <Box sx={{width: '100%', height: '100%'}}>
      <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            width: '100%',
            height: '100%',
            padding: props.isMobile ? '16px' : '24px',
            overflowY: 'auto',
            ...textSize(13),
        }}>
            <div style={{minHeight: props.isMobile ? '193px' : '258px', height: '100%', alignContent: 'center', textAlign: 'center'}}>{t("video_err_msg", '')}</div>
            <div className="linebreak" style={{borderTop: '1px solid', paddingTop: '10px', marginTop: '10px'}}>{t("video_err_remarks", '')?.replaceAll('{0}', ' • ')}</div>
        </div>
    </Box>
  ) : <>
    <ReactPlayer 
      key={`${curVideoSourceType}-${curQuality}`}
      ref={videoPlayerRef} //updating the react player ref
      url={isMi ? blobUrl[curVideoSourceType || VIDEOMODE_S] : videoUrl}
      height="100%"
      width="100%"
      playing={isPlaying}
      volume={volume}
      muted={muted}
      onProgress={progressHandler}
      onBuffer={bufferStartHandler}
      onBufferEnd={bufferEndHandler}
      onReady={readyHandler}
      onEnded={endedHandler}
      onError={(e) => errorHandler(e)}
      playsinline
      x5-video-player-type="h5-page" x5-video-player-fullscreen="false"
      playbackRate={playbackRate*speedWeight}
      config={{ // add for avoid error: net::ERR_CACHE_OPERATION_NOT_SUPPORTED
        file: {
          attributes: {
            preload: isMi ? 'none' : 'metadata', // Prevents aggressive concurrent buffering
            crossOrigin: 'anonymous', // Ensures clean header handling across domains
            controlsList: 'nodownload' // Optional: prevents download button conflicts
          }
        }
      }}
      />
    {
      // showErrMsg ?  
      // (
      //   <Box className="screenContainer">
      //     <span style={{color: "white"}}>video load failed</span>
      //   </Box>
      // ):
      (
        buffer || switchSrcData.current.isSwitching ?
        <Box className="screenContainer">
          <CircularProgress disableShrink />
        </Box> :
        <Box className="screenContainer" onClick={(e) => playPauseHandler(e)}>
          {speedChangeRegion('left', 0.5)}
          <div className="playIconContainer" style={{opacity: isPlaying ? 0: (switchSrcData?.current.seconds==-1 ? 1 : 0)}}>
            { props?.customPlayIconSlot ? props?.customPlayIconSlot : <PlayCircle /> }
          </div>
          {speedChangeRegion('right', 2)}
          {speedWeight != 1 && <Box className="spdTooltip">{speedWeight}x</Box>}
        </Box>
      )
    }
  </>

  const showOverridedContent = () => props.overridedContentSlot ? true : false

  const showControl = () => !(showOverridedContent() || showErrMsg)

  const placeHolder = <Box sx={{minWidth: '24px', marginRight: 'auto'}}/>
  return (
    <Box ref={playerContainerRef}
        sx={props.isEmbed ? {
          position: "relative",
          ...commonStyle()
        } :{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: '#2f2f2f !important',
          zIndex: 999,
          ...commonStyle()
          }}>
          { !props?.hideTitleBar && 
            <Grid className="title" item xs={12} sx={{
              display: 'flex',
              color: 'white',
              padding: '8px 10px',
              height: '40px',
              backgroundColor: props.title ? theme.palette.primary.main : 'transparent',
              zIndex: 1,
              ...props.titleSx
            }}>
              {
                props.title && (
                <>
                {props?.titleAlign !== 'left' && props?.handleClose && placeHolder}
                <div id="modal-title" style={{
                  maxHeight: '100%',
                  fontWeight: 500,
                  ...textSize(15),
                  textAlign: props?.titleAlign || 'center',
                  lineHeight: '18px',
                  width: '100%',
                  display: '-webkit-box',
                  '-webkit-line-clamp': '1',
                  '-webkit-box-orient': 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  alignSelf: 'center',
                } as React.CSSProperties}>
                  {props.title}
                </div>
                </>)
              }
              { 
                props?.handleClose && 
                <Box className="closeBtn" onClick={(e) => onCloseHandler(e)} >
                  <img src={`${myContextState.assetPrefix}/close.svg`} />
                </Box> 
              }
            </Grid>
          }
      <Grid container sx={{ 
        color: 'white',
        height: "100%",
        width: "100%",
        alignItems: 'baseline', 
        justifyContent: 'space-between', 
        minHeight: videoHeight ? videoHeight : '125px',
        position: 'relative'}}>
        <Grid item columns={12} id="video" className="videoContainer" onMouseMove={mouseMoveHandler}>
        {
          showOverridedContent() ? props.overridedContentSlot : renderPlayer()
        }
        </Grid>
        {
          showControl() && 
          <Grid item columns={12} className="controlWrapper">
            <Control
              ref={controlRef}
              assetPrefix={myContextState.assetPrefix}
              isMobile={props.isMobile}
              multiVideoSource={isMultiVideoSourceArray(props.sources) ? props.sources : undefined}
              curVideoSource={curVideoSource}
              curVideoSourceType={curVideoSourceType}
              curQuality={curQuality}
              isPlaying={isPlaying}
              ended={ended}
              currentTime={currentTime}
              duration={duration}
              played={played}
              loaded={loaded}
              mute={muted}
              volume={volume}
              fullscreen={fullscreen}
              onPlayPause={playPauseHandler}
              onSeek={seekHandler}
              onSeekMouseUp={seekMouseUpHandler}
              onToggleMute={toggleMute}
              onVolumeChangeHandler={volumeChangeHandler}
              onVolumeSeekUp={volumeSeekUpHandler}
              onChangeQuality={changeQualityHandler}
              onChangeSource={changeSourceHandler}
              onToggleFullscreen={toggleFullscreen}
              showCurrentTooltip={props.showCurrentTooltip}
              showHoveredTooltip={props.showHoveredTooltip}
              dismissEffect={props.controlDismissEffect}
              speedWeightOptions={[0.5,1,2]}
              speedWeight={speedWeight}
              onChangeSpeed={changeSpeedHandler}
              enableSpeedChange={props.enableSpeedChange} />
          </Grid>
        }
      </Grid>
    </Box>
  )
}

export default Player;